import Layout from '@gakki/components/Layout';
import React, {
  HTMLAttributes,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import AppContext, {StepStatus} from '@gakki/context/app-context';
import ProgressBoard from '@gakki/components/ProgressBoard';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useAuthContext, useClient} from '@gakki/context/auth-context';
import {useCompanyContext} from '@gakki/context/company-context';
import {toast} from 'react-toastify';
import {ReactComponent as PersonIcon} from '@gakki/assets/person.svg';
import {ReactComponent as RocketIcon} from '@gakki/assets/rocket.svg';
import {ReactComponent as MoneyIcon} from '@gakki/assets/money.svg';
import {ImportStatus, AuthURL, WebURL} from '@gakki/model/types';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faTimes, faCheck, faMinus} from '@fortawesome/pro-light-svg-icons';
import {ImportStatusResponse, UserInfoResponse} from '@gakki/api/types';
import ThumbUpImg from '@gakki/assets/thumb-up.png';
import {useXlsExport} from 'hooks/useXlsExport';
import cx from 'classnames';
import * as signalR from '@microsoft/signalr';
import useLocalStorage from '@common/hooks/use-local-storage';
import TextDialog from '@gakki/components/Dialog/TextDialog';
import {ReactComponent as Group} from '@common/assets/group-10482.svg';
import QRCodeIOS from '@common/assets/xe_app_store.jpg';
import QRCodeAndroid from '@common/assets/xe_google_play.jpg';

const Form: React.FC<HTMLAttributes<HTMLDivElement> & { enablePY: boolean }> = ({
  children,
  className,
  enablePY,
  ...props
}) => {
  return (
    <div
      {...props}
      className={cx(
        'min-h-[610px] bg-white rounded border border-[#e3e4e6] p-[13px] pt-0 m-auto', {
          'w-[1015px]': enablePY,
          'w-[850px]': !enablePY,
        },
        className,
      )}
    >
      {children}
    </div>
  );
};

const FormTitle: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <div
      className={cx(
        'text-h3 font-bold !leading-[61px] border-b border-[#e9eced]',
        className,
      )}
    >
      {children}
    </div>
  );
};

function InternalLink({
  redirect,
  children,
  auth,
  target = '_self',
}: {
  redirect?: string;
  children?: React.ReactNode;
  auth?: boolean;
  target?: string;
}) {
  let authPath = AuthURL.PRD;
  let webPath = WebURL.PRD;

  if (window.location.host.indexOf('localhost') > -1) {
    authPath = AuthURL.TST;
    webPath = WebURL.TST;
  }
  if (window.location.host.indexOf('tst-') > -1) {
    authPath = AuthURL.TST;
    webPath = WebURL.TST;
  }
  if (window.location.host.indexOf('uat-') > -1) {
    authPath = AuthURL.UAT;
    webPath = WebURL.UAT;
  }
  if (window.location.host.indexOf('pre-') > -1) {
    authPath = AuthURL.PRE;
    webPath = WebURL.PRE;
  }

  const href = auth ? authPath : `${webPath}${redirect ? redirect : ''}`;
  return (
    <a
      href={href}
      className="text-[#26abe3] cursor-pointer hover:underline"
      target={target}
    >
      {children ? children : href}
    </a>
  );
}
type ImportStepProps = {
  icon: ReactNode;
  status: ImportStatus;
  title: string;
};
function ImportStep({icon, title, status}: ImportStepProps) {
  let statusText = '';
  let statusIcon = null;
  let statusTextColor = 'text-[#b1b1b1]';
  switch (status) {
    case ImportStatus.Doing:
      statusText = '資料匯入中';
      statusIcon = (
        <span className="w-[7px] h-[7px] bg-[#b1b1b1] rounded-full mr-[7px] inline-block" />
      );
      statusTextColor = 'text-[#b1b1b1]';
      break;
    case ImportStatus.Failed:
      statusText = '資料匯入失敗';
      statusIcon = <FontAwesomeIcon icon={faTimes} className={'mr-[5px]'} />;
      statusTextColor = 'text-[#d63131]';
      break;
    case ImportStatus.Done:
      statusText = '資料匯入完成';
      statusIcon = <FontAwesomeIcon icon={faCheck} className={'mr-[5px]'} />;
      statusTextColor = 'text-[#3e4348]';
      break;
    case ImportStatus.ToDo:
    case ImportStatus.Untreated:
    default:
      statusText = '資料尚未匯入';
      statusIcon = (
        <span className="w-[7px] h-[7px] bg-[#b1b1b1] rounded-full mr-[7px] inline-block" />
      );
      statusTextColor = 'text-[#b1b1b1]';
      break;
  }
  const isToDo = [ImportStatus.Untreated, ImportStatus.ToDo].includes(status);
  return (
    <div
      className={cx(
        'w-[300px] pr-[15px] pl-[1px] mr-[30px] last:mb-0 bg-white z-[1]',
        'border rounded-[100px]',
        'relative flex items-center justify-between',
        'after:h-[1px] after:w-[30px] last:after:w-[0] last:after:h-[0]',
        'after:absolute after:right-[-46px] after:transform after:-translate-x-2/4',
        isToDo
          ? 'border-brand-black after:bg-brand-black text-brand-black'
          : 'border-[#26abe3] after:bg-[#26abe3] text-[#26abe3]',
      )}
    >
      <div className="flex items-center text-[15px]">
        <div className={'mr-3 '}>{icon}</div>
        {title}
      </div>
      <div className={`${statusTextColor} min-w-[100px] flex justify-center items-center`}>
        {status === ImportStatus.Doing ? (
          <div
            data-test-id={'active-status'}
            className="flex justify-start items-center after:content-[''] after:ml-0.5 after:text-red-500 after:border-l after:border-[#707070] after:h-[18px]  after:animate-typing-blink"
          >
            <div className="overflow-hidden text-base whitespace-nowrap w-full animate-typing-out">
              {statusText}
            </div>
          </div>
        ) : (
          <>
            {!isToDo && statusIcon}
            <span
              data-test-id={'active-status'}
              className="text-base"
            >
              {statusText}
            </span>
          </>
        )}
      </div>
    </div>
  );
}
type UserInfoProps =
  | {
      email: string;
      id: string;
      name: string;
      isActive: boolean;
    }
  | undefined;
function UserInfo({userData}: {userData: UserInfoProps}) {
  const {employee} = useCompanyContext();
  const {companyId} = useAuthContext();
  const api = useClient();
  const staff = employee.find(e => e.id === userData?.id);
  const employeeNumber = staff?.employeeNumber || '';
  const {setData: exportExcelData} = useXlsExport();
  return (
    <div
      className={
        'z-[1] text-[15px] leading-[2.27] flex mx-auto mt-[100px] mb-[140px] w-[960px] min-h-[400px] bg-[#fafafa] px-[30px] py-[40px]'
      }
    >
      <div className="w-[376px] mr-[85px] align-top">
        <div className={`mb-[20px] flex items-center`}>
          <span className="text-white rounded-2xl px-2.5 py-[5px] bg-[#26abe3] font-normal mr-2.5 text-[15px] h-[30px] flex items-center">
            Step 1
          </span>
          <span className="text-[#26abe3] font-medium text-xl">
            請登入Apollo XE
          </span>
        </div>
        <div className="w-full">
          <div className="text-[15px] leading-[2.27]">
            <span>前往登入 Web</span>
            <br />
            <InternalLink auth target="_blank" />
            <br />
            <span>前往下載 Apollo HR Express Edition App</span>
          </div>
          <div className="flex mt-[23px]">
            <div>
              <img src={QRCodeIOS} alt="ios" />
              <div className="text-[15px] text-center">iOS App Store</div>
            </div>
            <div>
              <img src={QRCodeAndroid} alt="android" />
              <div className="text-[15px] text-center">Android Google Play</div>
            </div>
          </div>
        </div>
        <p className={'mt-[15px] text-[15px]'}>
          系統管理員: {`${employeeNumber}/${userData?.name || ''}`}
          <br />
          登入帳號: {userData?.email}
          <br />
          密碼: Apollo加上系統管理員的出生月日
        </p>
        <p className={'mt-[10px] text-[13px] text-brand-black text-opacity-70'}>
          (例: 1月1日出生，預設密碼為Apollo0101)
          <br />
          若您未調整過預設資料，系統管理員生日為11/12，
          <br />
          預設密碼為 Apollo1112
        </p>
        <p className={'mt-[10px] text-[15px]'}>
          <span>驗證身分證字號: 系統管理員的身分證字號</span>
          <br />
          <span className="text-[13px] text-brand-black text-opacity-70">
            若您未調整過預設資料，系統管理員身分證字號為 D242222520
          </span>
        </p>
        <div className="w-full mt-[30px]">
          <span
            className={
              'w-[120px] h-[21]px mt-[10px] mr-3 text-[#26abe3] cursor-pointer text-[15px] hover:underline'
            }
            onClick={async () => {
              const res = await api.get(
                `productactive/report/excel/${companyId}`,
                {responseType: 'blob'},
              );
              exportExcelData({
                blob: res,
                name: `GakkiReport-${Date.now()}.xls`,
              });
            }}
          >
            下載設定參數報表
          </span>
        </div>
      </div>
      <div className="w-[calc(100%-461px)] flex flex-col h-full">
        <div className={`mb-[20px] flex items-start`}>
          <span className="text-white rounded-2xl px-2.5 py-[5px] bg-[#26abe3] font-normal mr-2.5 text-[15px] h-[30px] flex items-center whitespace-nowrap">
            Step 2
          </span>
          <span className="text-[#26abe3] font-medium text-xl">
            請記得進入Apollo XE完成設定，才可以開始使用喔！
          </span>
        </div>
        <div className="text-[15px]">
          以下提供常見設定，請務必注意。
          <ol>
            <li className="flex align-top">
              <div className="text-[15px] mr-[3px]">1. </div>
              <div className="text-[15px]">
                <InternalLink
                  redirect="/payroll/setting/parameters/salaryaccount"
                  target="_blank"
                >
                  薪資科目計算規則
                </InternalLink>
                ：請於薪資結算前確認。
              </div>
            </li>
            <li className="flex align-top">
              <div className="text-[15px] mr-[3px]">2. </div>
              <div className="text-[15px]">
                <InternalLink
                  redirect="/ta/manager/modulesetting/parameter/leavetypesname"
                  target="_blank"
                >
                  假別名稱維護
                </InternalLink>
                ：若貴公司有自訂假，請於此新增。
              </div>
            </li>
            <li className="flex align-top" style={{whiteSpace: 'nowrap'}}>
              <div className="text-[15px] mr-[3px]">3. </div>
              <div className="text-[15px]" style={{whiteSpace: 'pre-wrap'}}>
                <InternalLink
                  redirect="/ta/manager/modulesetting/parameter/attendance"
                  target="_blank"
                >
                  出勤規則設定
                </InternalLink>
                ：進入頁面後請先複製法規範本後再編輯。編輯內容包含假別規則、打卡規則、加班規則、月休設定、公出/出差/靈活辦公等設定。
              </div>
            </li>
            <li className="flex align-top">
              <div className="text-[15px] mr-[3px]">4. </div>
              <div className="text-[15px]">
                <InternalLink
                  redirect="/ta/manager/modulesetting/parameter/checkin/checkinremind"
                  target="_blank"
                >
                  打卡提醒設定
                </InternalLink>
              </div>
            </li>
            <li className="flex align-top">
              <div className="text-[15px] mr-[3px]">5. </div>
              <div className="text-[15px]">
                <InternalLink
                  redirect="/ta/manager/modulesetting/parameter/fields-setting"
                  target="_blank"
                >
                  欄位設定
                </InternalLink>
                ：可設定打卡、請假、加班的時間刻度等。
              </div>
            </li>
            <li className="flex align-top">
              <div className="text-[15px] mr-[3px]">6. </div>
              <div className="text-[15px]">
                <InternalLink
                  redirect="/ta/manager/modulesetting/approve/leaveform"
                  target="_blank"
                >
                  簽核設定
                </InternalLink>
                ：可設定組織簽核規則。
              </div>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default function StepActive() {
  const queryClient = useQueryClient();
  const api = useClient();
  const {companyId} = useAuthContext();
  const [showTextDialog, setShowTextDialog] = useState(false);
  const {refetchEnablePY, onPrev, updatedTime, setIsLoading, isDefault} =
    useContext(AppContext);
  const {enablePY} = useCompanyContext();
  const [, setStepStatus] = useLocalStorage<StepStatus>(
    'stepStatus',
    StepStatus.init,
  );
  const {data: userData} = useQuery<UserInfoResponse>('company-xeuser', () =>
    api.get(`user/company/${companyId}/xeuser`).then(d => d.data),
  );

  const {data, isLoading, isIdle, isFetching} = useQuery<ImportStatusResponse>({
    queryKey: 'import-status',
    queryFn: () =>
      api
        .get(`productactive/getstates?companyId=${companyId}`)
        .then(d => d.data),
    onSuccess: d => {
      if (d?.status === ImportStatus.Done) {
        setStepStatus(StepStatus.active);
      } else {
        setStepStatus(StepStatus.init);
      }
    },
  });
  const {mutate: activeSystem, isLoading: isActivating} = useMutation(
    () => refetchEnablePY('next', 12)
    .then(() => api.post(`productactive/runxeactivation/${companyId}`)),
    {
      onError: (error: any) => {
        if (error?.message !== 'NotEnablePY') {
          toast.error(error.message, {
            position: 'top-center',
            autoClose: false,
            hideProgressBar: true,
          });
        }
      },
      onSuccess: () => {
        queryClient.refetchQueries('import-status');
      },
    },
  );

  useEffect(() => {
    if (isIdle || isLoading || isFetching || isActivating) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [isIdle, isLoading, isFetching, isActivating, setIsLoading]);

  const [connection, setConnection] = useState<signalR.HubConnection>();

  const onMessageReceived = useCallback((response: any) => {
    if (Number(response.code) === 500) {
      toast.error(response?.message || '系統異常', {
        position: 'top-center',
        autoClose: false,
        hideProgressBar: true,
      });
    }
    const {status, fdStatus, ptStatus, pyStatus} = response.data;
    queryClient.setQueryData('import-status', {
      importStateId: data?.importStateId,
      status,
      fdStatus,
      ptStatus,
      pyStatus,
    });
    if (
      status === ImportStatus.Doing ||
      status === ImportStatus.ToDo ||
      status === ImportStatus.Untreated
    ) {
      setStepStatus(StepStatus.init);
    }
    if (status === ImportStatus.Done) {
      setStepStatus(StepStatus.active);
      connection?.stop();
    }
    if (status === ImportStatus.Failed) {
      setStepStatus(StepStatus.init);
      connection?.stop();
    }
  }, []);

  useEffect(() => {
    if (
      data &&
      data?.status !== ImportStatus.Done &&
      data?.status !== ImportStatus.Failed &&
      !connection
    ) {
      const _connection = new signalR.HubConnectionBuilder()
        .withUrl(`${window.location.origin}/activateHub?CompanyId=${companyId}`)
        .withAutomaticReconnect([0, 1000, 5000])
        .configureLogging(signalR.LogLevel.Information)
        .build();

      _connection.on('UpdateActivateStatus', onMessageReceived);
      _connection
        .start()
        .then(() => setConnection(_connection))
        .catch(err => console.error(err));
    }
  }, [data, connection, onMessageReceived]);

  useEffect(() => {
    isDefault && setShowTextDialog(true);
  }, []);

  let statusText = '';
  let statusTextColor = 'text-[#b1b1b1]';

  switch (data?.status) {
    case ImportStatus.Done:
      statusText = '恭喜您完成導入精靈的建置！';
      statusTextColor = 'text-[#26abe3]';
      break;
    case ImportStatus.Failed:
      if (enablePY === false && data?.pyStatus === ImportStatus.Failed) {
        statusText = '系統預設值匯入失敗。';
      } else {
        statusText = '資料匯入失敗。';
      }
      statusTextColor = 'text-[#d63131]';
      break;
    case ImportStatus.Doing:
    default:
      if (enablePY === false && data?.pyStatus === ImportStatus.Doing) {
        statusText = '系統預設值匯入中，請耐心等候...';
      } else {
        statusText = '資料匯入中，請耐心等候...';
      }
      statusTextColor = 'text-[#26abe3]';
      break;
  }

  return (
    <React.Fragment>
      {data === null && (
        <Form enablePY={enablePY}>
          <FormTitle>恭喜您完成導入精靈的建置！</FormTitle>
          <div className={'flex items-center text-[15px] pt-[46.6px] px-5'}>
            <div>
              <p className={'mb-10 text-[15px]'}>
                Hi,
                <br />
                此階段，您可回頭查閱對應參數設定。
                <br />
                <br />
              </p>
              <ProgressBoard active enablePY={enablePY}/>
            </div>
          </div>
        </Form>
      )}
      {data && (
        <Layout.MainContent className={'relative'}>
          <div
            className={`${statusTextColor} text-xl mx-auto mt-[57px] mb-[38px] font-medium`}
          >
            <span data-test-id={'active-status'}>{statusText}</span>
          </div>
          <div className={'flex items-center justify-center'}>
            <ImportStep
              icon={<PersonIcon className={'h-[48px] w-[48px]'} />}
              status={data.fdStatus}
              title={'人事組織'}
            />
            <ImportStep
              icon={<RocketIcon className={'h-[48px] w-[48px]'} />}
              status={enablePY ?
                data.ptStatus :
                (data.ptStatus === ImportStatus.Done ?
                  (data.pyStatus === ImportStatus.ToDo ? ImportStatus.Doing : data.pyStatus) :
                data.ptStatus)
              }
              title={'行動出勤'}
            />
            {
              enablePY &&
              <ImportStep
                icon={<MoneyIcon className={'h-[48px] w-[48px]'} />}
                status={data.pyStatus}
                title={'智慧結薪'}
              />
            }
          </div>
          {data.fdStatus === ImportStatus.Done &&
          data.ptStatus === ImportStatus.Done &&
          data.pyStatus === ImportStatus.Done ? (
            <UserInfo userData={userData} />
          ) : (
            <p
              className={
                'text-center text-base leading-[2.27] mx-auto mt-[75px] mb-[72px]'
              }
            >
              客服電話: (03) 659-1588
              <br />
              客服信箱: ApolloGSC@mayohr.com
            </p>
          )}
          <img
            src={ThumbUpImg}
            className={'absolute right-0 bottom-0 transform -translate-y-2/4'}
          />
        </Layout.MainContent>
      )}
      <TextDialog
        isOpen={showTextDialog}
        onConfirm={() => setShowTextDialog(false)}
        onCancel={() => setShowTextDialog(false)}
        title={'系統訊息'}
        content={
          <div className="w-full flex justify-between mt-4 mb-[61px]">
            <div className="flex flex-col items-start text-[15px] leading-[30px]">
              <span>請點擊啟用專區，</span>
              <span>開始試用 XE 系統。</span>
            </div>
            <div className="mt-2 mr-[27px]">
              <Group />
            </div>
          </div>
        }
      />
      <Layout.Footer
        showPrevButton={data?.status !== ImportStatus.Done}
        showActiveButton={data?.status !== ImportStatus.Done}
        onPrev={onPrev}
        onActive={activeSystem}
        updatedTime={updatedTime}
        disabledPrevButton={
          data?.status === ImportStatus.ToDo ||
          data?.status === ImportStatus.Doing ||
          data?.status === ImportStatus.Done ||
          isLoading ||
          isActivating
        }
        disabledActiveButton={
          data?.status === ImportStatus.ToDo ||
          data?.status === ImportStatus.Doing ||
          data?.status === ImportStatus.Done ||
          isLoading ||
          isActivating
        }
      />
    </React.Fragment>
  );
}
